import { Component } from '@theme/component';
import { ThemeEvents } from '@theme/events';

/**
 * Kaart die de behangcalculator van de app opent en het uitgerekende aantal
 * in de buybox zet (SHO-9545, SHO-9689).
 *
 * De calculator zelf zit in een app-block waarvan alleen de knoptekst instelbaar is. Dit
 * blok zet er de uitleg omheen die op de PDP hoort te staan — wat je krijgt en wat het kost
 * aan moeite — en klikt bij gebruik gewoon de knop van de app aan. Dus geen tweede ingang:
 * de app-knop is verborgen. De host zelf rendert Liquid alleen bij calculator-capability
 * (patroon + afmeting), zodat hij niet op andere PDP’s verschijnt of later inklapt.
 *
 * Sinds SHO-9689 legt de calculator niets meer zelf in de winkelwagen. Hij meldt met
 * `wallpaper-calculator:quantity` welk aantal eruit kwam; wij vangen dat af
 * (`preventDefault`) en zetten het in de aantalteller van dít formulier. De app heeft een
 * eigen terugval voor thema's die niet luisteren — die mag hier nooit aan bod komen, want
 * dan raadt de app welk `[name="quantity"]` de buybox is.
 *
 * Waarom de teller en niet de winkelwagen: het antwoord blijft zichtbaar en corrigeerbaar
 * naast de prijs. De klant ziet wat 8 rollen kosten vóór hij ze koopt, en houdt één plek
 * waar het aantal staat in plaats van twee die uit elkaar kunnen lopen.
 *
 * De orderregel krijgt daarnaast dezelfde verborgen eigenschappen mee die de app zette
 * toen hij nog zelf in de winkelwagen legde, zodat een bestelling zelf blijft vertellen
 * dat er gerekend is (SHO-9301).
 *
 * @extends {Component}
 */

/** Id van de app-knop die deze regel opent. */
const APP_TRIGGER_ID = 'wallpaper-calculator-button';

/** Event van de app-extension: "dit is het aantal dat eruit komt". */
const QUANTITY_EVENT = 'wallpaper-calculator:quantity';

/** Formulier van de buybox, voor het geval de aantalteller er niet naar wijst. */
const BUYBOX_FORM = 'product-form-component form[action*="/cart/add"], form[action*="/cart/add"]';

/**
 * Verborgen eigenschappen voor de orderregel — met exact de namen die de app gebruikte
 * toen die nog zelf in de winkelwagen legde. Andere namen zouden de analyse in tweeën
 * knippen op de dag dat dit live gaat.
 */
const CART_PROPERTY_GEBRUIKT = '_hds_wallpaper_calculator';
const CART_PROPERTY_AANTAL = '_hds_wallpaper_calc_qty';
const CART_PROPERTY_EENHEID = '_hds_wallpaper_calc_unit';

/**
 * Aantalveld van de buybox — nooit dat van een winkelwagenregel, quick-add of drawer.
 *
 * De PDP-teller is sinds de plus/min-knoppen een `<input type="number">` in een
 * `quantity-selector-component`; op andere plekken (en in oudere opstellingen) staat er
 * een `<select>`. Beide worden hier gevonden; regels in de winkelwagen dragen
 * `data-cart-line` en vallen af.
 */
const QUANTITY_FIELD = [
  'quantity-selector-component [name="quantity"]:not([data-cart-line])',
  'product-form-component [name="quantity"]',
  'form[action*="/cart/add"] [name="quantity"]',
].join(', ');

class WallpaperPlan extends Component {
  /** Aantal dat wij het laatst hebben gezet; wijkt het veld daarvan af, dan klopt de melding niet meer. */
  #gezetAantal = null;

  /** Staat aan terwijl wíj het veld bijwerken, zodat onze eigen zet niet als klantactie telt. */
  #eigenZet = false;

  connectedCallback() {
    super.connectedCallback();
    this.addEventListener('click', this.#onClick);
    document.addEventListener(QUANTITY_EVENT, this.#onQuantity);
    document.addEventListener(ThemeEvents.quantitySelectorUpdate, this.#onQuantityFieldChange);
    document.addEventListener('change', this.#onQuantityFieldChange);
  }

  disconnectedCallback() {
    this.removeEventListener('click', this.#onClick);
    document.removeEventListener(QUANTITY_EVENT, this.#onQuantity);
    document.removeEventListener(ThemeEvents.quantitySelectorUpdate, this.#onQuantityFieldChange);
    document.removeEventListener('change', this.#onQuantityFieldChange);
    super.disconnectedCallback();
  }

  /** @param {Event} event */
  #onClick = (event) => {
    const target = event.target;
    if (!(target instanceof Element) || !target.closest('[data-wpp-open]')) return;

    const appKnop = document.getElementById(APP_TRIGGER_ID);
    if (!(appKnop instanceof HTMLElement)) return;

    event.preventDefault();
    appKnop.click();
  };

  /** @param {Event} event */
  #onQuantity = (event) => {
    const detail = /** @type {CustomEvent} */ (event).detail;
    const aantal = Math.round(Number(detail?.quantity));
    if (!Number.isFinite(aantal) || aantal < 1) return;

    // Vanaf hier is het onze verantwoordelijkheid; de terugval van de app moet uit blijven.
    event.preventDefault();

    const gezet = this.#zetAantal(aantal);
    this.#merkOrderregel(aantal, detail?.unit);
    this.#toonBevestiging(gezet ?? aantal, detail?.unitLabel, gezet !== null);
  };

  /**
   * Zet het aantal in de teller en laat het thema er zelf op reageren.
   *
   * Teruggave = het aantal dat er ná afloop écht staat, of `null` als er geen veld is. De
   * teller mag klemmen (`data-max`), en dan is dát het getal dat de klant koopt — de
   * bevestiging hoort geen aantal te melden dat nergens staat.
   *
   * @param {number} aantal
   * @returns {number | null}
   */
  #zetAantal(aantal) {
    const veld = this.#aantalVeld();
    if (!veld) return null;

    // De select is opgebouwd tot `data-max` (standaard 100) en kan een increment-regel
    // volgen; een uitkomst die daar niet in zit heeft dus geen <option>. Zonder deze regel
    // valt de waarde stil terug op de eerste optie — 1 rol voor een muur van 12 meter.
    if (veld instanceof HTMLSelectElement && !Array.from(veld.options).some((optie) => optie.value === String(aantal))) {
      const optie = document.createElement('option');
      optie.value = String(aantal);
      optie.textContent = String(aantal);
      veld.appendChild(optie);
    }

    veld.value = String(aantal);
    // De teller heeft eigen regels (min/max/increment) en laat de rest van het thema
    // meelopen. Die logica roepen we aan via zijn eigen ingang in plaats van hem hier na
    // te bouwen: het nummerveld luistert op `blur`, de dropdown op `change`.
    this.#eigenZet = true;
    veld.dispatchEvent(new Event(veld instanceof HTMLSelectElement ? 'change' : 'blur', { bubbles: true }));
    this.#eigenZet = false;

    const gezet = Math.round(Number(veld.value));
    return Number.isFinite(gezet) && gezet > 0 ? gezet : null;
  }

  /**
   * Aantalteller van de buybox, of `null` als deze PDP er geen heeft.
   *
   * @returns {HTMLInputElement | HTMLSelectElement | null}
   */
  #aantalVeld() {
    const bereik = this.closest('.shopify-section') ?? document;
    const veld = bereik.querySelector(QUANTITY_FIELD) ?? document.querySelector(QUANTITY_FIELD);
    if (veld instanceof HTMLSelectElement || veld instanceof HTMLInputElement) return veld;
    return null;
  }

  /**
   * Formulier waarmee dit product in de winkelwagen belandt.
   *
   * Eerst via de aantalteller: die hoort per definitie bij het formulier dat straks
   * verstuurd wordt — ook als hij er buiten staat en er alleen met `form=` aan hangt.
   * Zonder teller blijft het formulier in dezelfde sectie over.
   *
   * @returns {HTMLFormElement | null}
   */
  #buyboxFormulier() {
    const veld = this.#aantalVeld();
    if (veld?.form) return veld.form;

    const bereik = this.closest('.shopify-section') ?? document;
    const formulier = bereik.querySelector(BUYBOX_FORM);
    return formulier instanceof HTMLFormElement ? formulier : null;
  }

  /**
   * Laat op de orderregel achter dat dit aantal uit de calculator komt.
   *
   * Toen de popup nog zelf in de winkelwagen legde, hing hij deze drie eigenschappen aan
   * de regel. Sinds SHO-9689 doet hij dat niet meer, en daarmee verdween het enige spoor
   * in de bestelling zelf: wie de calculator gebruikte was daarna alleen nog uit
   * GA4-sessies af te leiden — waar iedereen ontbreekt die cookies weigert.
   *
   * Het aantal blijft staan wat de calculator zéí, ook als de klant het daarna bijstelt.
   * Juist het verschil tussen dat getal en de gekochte hoeveelheid is wat je bij retouren
   * en nabestellingen wilt kunnen zien.
   *
   * @param {number} aantal aantal zoals de calculator het uitrekende
   * @param {string | undefined} eenheid
   */
  #merkOrderregel(aantal, eenheid) {
    const formulier = this.#buyboxFormulier();
    if (!formulier) return;

    this.#zetEigenschap(formulier, CART_PROPERTY_GEBRUIKT, '1');
    this.#zetEigenschap(formulier, CART_PROPERTY_AANTAL, String(aantal));
    this.#zetEigenschap(formulier, CART_PROPERTY_EENHEID, eenheid === 'meter' ? 'meter' : 'rol');
  }

  /**
   * Eén verborgen eigenschap, aangemaakt of bijgewerkt.
   *
   * De velden hangen aan dít blok en staan niet ín het formulier: dat hoort bij een ander
   * blok en wordt bij een variantwissel opnieuw getekend. Het `form`-attribuut zorgt dat
   * ze tóch meelopen in `new FormData(formulier)`. Heeft het formulier geen id — de
   * opstelling waarin de teller er gewoon in staat — dan gaan ze er alsnog in.
   *
   * @param {HTMLFormElement} formulier
   * @param {string} naam
   * @param {string} waarde
   */
  #zetEigenschap(formulier, naam, waarde) {
    const veldnaam = `properties[${naam}]`;
    const selector = `input[name="${veldnaam}"]`;
    let veld = this.querySelector(selector) ?? formulier.querySelector(selector);

    if (!(veld instanceof HTMLInputElement)) {
      veld = document.createElement('input');
      veld.type = 'hidden';
      veld.name = veldnaam;
      // Niet `formulier.id`: in de browser is dat het hidden variant-veld
      // `name="id"`, geen string. `getAttribute('id')` is het echte formulier-id.
      const formulierId = formulier.getAttribute('id');
      if (formulierId) {
        veld.setAttribute('form', formulierId);
        this.appendChild(veld);
      } else {
        formulier.appendChild(veld);
      }
    }

    veld.value = waarde;
  }

  /**
   * @param {number} aantal
   * @param {string | undefined} eenheid
   * @param {boolean} geplaatst
   */
  #toonBevestiging(aantal, eenheid, geplaatst) {
    const rij = this.querySelector('[data-wpp-row]');
    const klaar = this.querySelector('[data-wpp-done]');
    const tekst = this.querySelector('[data-wpp-done-text]');
    const sub = this.querySelector('[data-wpp-done-sub]');
    if (!(rij instanceof HTMLElement) || !(klaar instanceof HTMLElement)) return;

    const woord = eenheid || (aantal === 1 ? 'rol' : 'rollen');
    if (tekst instanceof HTMLElement) tekst.textContent = `${aantal} ${woord}`;
    if (sub instanceof HTMLElement) {
      sub.textContent = geplaatst
        ? sub.dataset.wppDoneSub || ''
        : sub.dataset.wppDoneSubManual || '';
    }

    this.#gezetAantal = geplaatst ? aantal : null;
    rij.hidden = true;
    klaar.hidden = false;
  }

  /**
   * Past de klant het aantal daarna zelf aan, dan gaat de bevestiging weg: hij zou anders
   * een uitkomst bevestigen die niet meer in de teller staat.
   *
   * Twee ingangen, want de teller kent twee manieren: typen geeft `change`, de plus- en
   * minknoppen zetten de waarde in code en melden dat met `quantity-selector:update`.
   *
   * @param {Event} event
   */
  #onQuantityFieldChange = (event) => {
    if (this.#eigenZet || this.#gezetAantal === null) return;
    const veld = event.target;
    if (!(veld instanceof HTMLElement) || !veld.matches(QUANTITY_FIELD)) return;
    if (Math.round(Number(/** @type {HTMLInputElement} */ (veld).value)) === this.#gezetAantal) return;

    this.#gezetAantal = null;
    const rij = this.querySelector('[data-wpp-row]');
    const klaar = this.querySelector('[data-wpp-done]');
    if (rij instanceof HTMLElement) rij.hidden = false;
    if (klaar instanceof HTMLElement) klaar.hidden = true;
  };
}

if (!customElements.get('wallpaper-plan')) {
  customElements.define('wallpaper-plan', WallpaperPlan);
}
